/**
 * components/riskTerminal/completed087.js
 *
 * upstox-087 — the COMPLETED TODAY list, to the Claude Design handoff (positions-wide-handoff-2026-11-04, section 2):
 * BUY / SELL lanes, a four-item row, readable contract names, rejected-row states and filters. Pure functions; the
 * component is CompletedOrders.jsx. Colour law (HANDOVER 2026-11-04): a direction is never coloured — the BUY and SELL
 * lanes use the same ink; red / amber mean rule state only (an undismissed rejection, a missing intent).
 */

import { orderCharges, limitWords } from './model';

export const INK = '#1a1917';
export const GREY = '#aba396';           // a finished row
export const RED = '#c1301a';            // a rejection not yet dismissed
export const RED_MUTED = '#a8645a';      // a dismissed rejection's word

export const NARROW_BELOW = 400;         // < 400 px of column: SX / NF / BNF, 11.5 px rows, "rej." / "cxl", 3-letter intent
export const EXTRA_FROM = 480;           // ≥ 480 px of column: TYPE and CHARGES appear (when the name still fits)
export const NAME_MIN = 112;             // the contract name is never cut: the extra columns need this much left for it
export const ROW_H = 30;
export const ABBREV = { SENSEX: 'SX', NIFTY: 'NF', BANKNIFTY: 'BNF' };
export const FILTERS = ['all', 'buys', 'sells', 'rejected', 'untagged'];

// ── the column geometry ───────────────────────────────────────────────────

/**
 * What a column of `w` px shows. `w` unknown (null / 0) → the 1920 frame's layout (440 px).
 *   narrow  < 400: the handoff's 28 · 1fr · 88 · 88 · 30 grid at 11.5 px.
 *   regular      : 34 · 1fr · 92 · 92 · 44 at 12.5 px.
 *   extra  ≥ 480 : TYPE and CHARGES between SELL and INTENT — compact, and only while the contract name keeps NAME_MIN px.
 */
export function completedLayout(w) {
  const width = w > 0 ? w : 440;
  const narrow = width < NARROW_BELOW;
  if (narrow) return { narrow, extra: false, cols: '28px minmax(70px,1fr) 88px 88px 30px', fs: 11.5, pad: 10, gap: 6, w: width };
  const compact = { cols: '34px minmax(0,1fr) 80px 80px 58px 44px 44px', gap: 4, pad: 12, fixed: 24 + 24 + 34 + 160 + 58 + 44 + 44 };
  if (width >= EXTRA_FROM && width - compact.fixed >= NAME_MIN) return { narrow, extra: true, cols: compact.cols, fs: 12.5, pad: compact.pad, gap: compact.gap, w: width };
  return { narrow, extra: false, cols: '34px minmax(0,1fr) 92px 92px 44px', fs: 12.5, pad: 14, gap: 6, w: width };
}

// ── the contract name ─────────────────────────────────────────────────────

/** "SENSEX 76300 PE 05 NOV 26" → { u, k, o, date } (date "05 NOV", the year dropped); null when it is not an option name. */
export function parseContract(sym) {
  const m = String(sym || '').match(/^(\S+) (\d+(?:\.\d+)?) (CE|PE)(?: (\d{2} [A-Z]{3})(?: \d{2})?)?$/);
  return m ? { u: m[1], k: m[2], o: m[3], date: m[4] || null } : null;
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-11-05" → "5 Nov" */
export function dateWords(iso) {
  const m = String(iso || '').match(/^\d{4}-(\d{2})-(\d{2})/);
  return m ? Number(m[2]) + ' ' + MON[Number(m[1]) - 1] : null;
}

/**
 * The contract as the row writes it.
 *   hidden expiry : the order's expiry is the underlying's nearest (`nearest[underlying]`) → never written.
 *   ring          : any other expiry → a 5 px ring after the strike; the date is in the hover.
 *   unknown       : the order's expiry or the nearest is not known → the date stays in the name (nothing is hidden on a guess).
 *   narrow        : SENSEX → SX, NIFTY → NF, BANKNIFTY → BNF.
 * The strike and CE / PE are never cut. → { name, full, ring, expiry (words), abbreviated }
 */
export function contractView(o, sym, nearest, narrow) {
  const p = parseContract(sym);
  const exp = o && o.expiry ? String(o.expiry).slice(0, 10) : null;
  const near = p && nearest ? nearest[o.underlying || p.u] || nearest[p.u] || null : null;
  const abbr = u => (narrow && ABBREV[u]) || u;
  if (!p) {
    const m = String(sym || '').match(/^(\S+)(.*)$/);
    const noYr = String(sym || '').replace(/^(.* \d{2} [A-Z]{3}) \d{2}$/, '$1');
    const name = m && narrow && ABBREV[m[1]] ? ABBREV[m[1]] + noYr.slice(m[1].length) : noYr;
    return { name, full: String(sym || ''), ring: false, expiry: dateWords(exp), abbreviated: name !== noYr };
  }
  const known = !!exp && !!near;
  const hidden = known && exp <= near;
  const ring = known && !hidden;
  const base = abbr(p.u) + ' ' + p.k + ' ' + p.o;
  const name = known || !p.date ? base : base + ' ' + p.date;
  return { name, full: p.u + ' ' + p.k + ' ' + p.o, ring, expiry: dateWords(exp) || (p.date ? p.date.replace(/^0/, '') : null), abbreviated: narrow && !!ABBREV[p.u] };
}

// ── a row ─────────────────────────────────────────────────────────────────

export const groupOf = o => {
  const st = String(o.order_status || '').toLowerCase();
  return o.status_group || (st === 'rejected' ? 'REJECTED' : st === 'cancelled' ? 'CANCELLED' : st === 'complete' ? 'FILLED' : 'OPEN');
};

/** True for a broker tag that is not ours (ours: the fenced order test's TT-…). */
export const anotherApp = tag => !!tag && !/^TT-/i.test(String(tag));

const priceOf = o => {
  const type = String(o.order_type || '').toUpperCase();
  return o.price > 0 ? Number(o.price).toFixed(2) : type === 'SL-M' ? 'SL-M' : type === 'MARKET' ? 'MKT' : '—';
};

/**
 * The lane cell. state: filled · part · cancelled · rejected (not yet dismissed) · dismissed.
 *   filled        `780 @ 76.05`            ink 600
 *   part          `300/800 @ 55.02`        ink 600 (the rest, cancelled, is in the hover)
 *   cancelled     `650 cancelled`          grey #aba396, 400
 *   rejected      `1000 rejected`          red #c1301a, 600, with the 3 px red rule
 *   dismissed     `1000 rejected`          muted red #a8645a, 400; the row finished grey; no rule
 * Fill price = the order's average fill; none reported → the order price, with the reason for the hover.
 * → { side, state, text, color, weight, rule, rowGrey, reason }
 */
export function laneView(o, narrow) {
  const side = String(o.side || '').toUpperCase() === 'BUY' ? 'BUY' : 'SELL';
  const grp = groupOf(o);
  const q = o.quantity, f = o.filled_quantity || 0;
  const at = narrow ? '@' : ' @ ';
  if (grp === 'REJECTED') {
    const dismissed = !!o.rejection_dismissed;
    return { side, state: dismissed ? 'dismissed' : 'rejected', text: (q ?? '—') + (narrow ? ' rej.' : ' rejected'),
      color: dismissed ? RED_MUTED : RED, weight: dismissed ? 400 : 600, rule: !dismissed, rowGrey: dismissed, reason: null };
  }
  if (f <= 0) {
    return { side, state: 'cancelled', text: (q ?? '—') + (narrow ? ' cxl' : ' cancelled'), color: GREY, weight: 400, rule: false, rowGrey: true, reason: null };
  }
  const avg = Number(o.average_price);
  const has = o.average_price != null && Number.isFinite(avg) && avg > 0;
  const part = q != null && f < q;
  return { side, state: part ? 'part' : 'filled', text: (part ? f + '/' + q : f) + at + (has ? avg.toFixed(2) : priceOf(o)),
    color: INK, weight: 600, rule: false, rowGrey: false, reason: has ? null : 'fill price not reported' };
}

/** The intent cell: { kind: 'none' | 'word' | 'tag', text, full }. Cancelled and rejected orders need no intent. */
export function intentView(o, narrow) {
  const grp = groupOf(o);
  if (grp === 'REJECTED' || !((o.filled_quantity || 0) > 0)) return { kind: 'none', text: '', full: '' };
  if (o.intent) return { kind: 'word', text: narrow ? String(o.intent).slice(0, 3) : o.intent, full: o.intent };
  return { kind: 'tag', text: 'TAG', full: '' };
}

/** TYPE in words: "market" / "limit 76.00" / "SL 75.00 trg 74.00" / "SL-M trg 74.00". */
export function typeWords(o) {
  const type = String(o.order_type || '').toUpperCase();
  const lim = limitWords(o);
  if (type === 'MARKET') return 'market';
  if (type === 'LIMIT') return lim || 'limit';
  if (type === 'SL') return 'SL' + (o.price > 0 ? ' ' + Number(o.price).toFixed(2) : '') + (o.trigger_price > 0 ? ' trg ' + Number(o.trigger_price).toFixed(2) : '');
  if (type === 'SL-M') return 'SL-M' + (o.trigger_price > 0 ? ' trg ' + Number(o.trigger_price).toFixed(2) : '');
  return lim || (type ? type.toLowerCase() : '—');
}

const hhmmss = ts => String(ts || '').slice(11, 19);
const hhmm = ts => String(ts || '').slice(11, 16);
const isoTime = iso => { const m = String(iso || '').match(/T(\d{2}:\d{2})/); return m ? m[1] : null; };

/**
 * The hover card for a row: { head, text } (the shared card: a head and lines). Exact time, contract with expiry,
 * side + qty @ average, type, charges, "Placed by <tag> (another app)", intent and when set, order id, the broker's
 * reject reason, the partial-fill detail.
 */
export function hoverOf(o, sym, view, lane, flag) {
  const grp = groupOf(o);
  const q = o.quantity, f = o.filled_quantity || 0;
  const lines = [];
  lines.push(view.full + (view.expiry ? ' · ' + view.expiry : ''));
  if (grp === 'REJECTED') {
    lines.push('Side: ' + lane.side + ' ' + (q ?? '—') + ' @ ' + typeWords(o));
    lines.push('Rejected: ' + (o.status_message || 'no reason given'));
  } else if (f <= 0) {
    lines.push('Side: ' + lane.side + ' ' + (q ?? '—') + ' @ ' + typeWords(o));
    lines.push('Cancelled, nothing filled' + (o.status_message ? ' — ' + o.status_message : ''));
  } else {
    lines.push('Side: ' + lane.side + ' ' + f + ' @ ' + lane.text.split(/@ ?/).pop() + (lane.reason ? ' (order price)' : ' avg'));
    if (lane.reason) lines.push('Fill price not reported — the order price is shown');
    if (q != null && f < q) lines.push('Partly filled: ' + f + ' of ' + q + (grp === 'CANCELLED' ? '; the other ' + (q - f) + ' cancelled' : ''));
  }
  lines.push('Type: ' + typeWords(o));
  const ch = orderCharges(o.charges);
  if (ch) lines.push('Charges: ' + (ch.unknown ? 'unknown — ' + String(ch.title).replace(/^Charges unknown — /, '') : ch.text.replace(/^≈/, '≈')));
  if (anotherApp(o.tag)) lines.push('Placed by ' + o.tag + ' (another app)');
  if (o.intent) {
    const at = isoTime(o.intent_declared_at);
    lines.push('Intent: ' + o.intent + (at ? ' · set ' + at : ''));
  }
  if (flag) lines.push(flag + ' — placed after the day limit');
  lines.push('Order ID: ' + o.upstox_order_id);
  return { head: 'ORDER · ' + (hhmmss(o.ts) || hhmm(o.ts)), text: lines.join('\n') };
}

// ── the filters ───────────────────────────────────────────────────────────

/** Each filter's rule; they only hide, never reorder. */
export const FILTER_TEST = {
  all: () => true,
  buys: o => String(o.side || '').toUpperCase() === 'BUY',
  sells: o => String(o.side || '').toUpperCase() !== 'BUY',
  rejected: o => groupOf(o) === 'REJECTED',
  untagged: o => intentView(o, false).kind === 'tag',
};

export function applyFilter(items, filter) {
  const t = FILTER_TEST[filter] || FILTER_TEST.all;
  return items.filter(t);
}

/** The counts the filter line shows: { all, buys, sells, rejected, fresh (undismissed rejections), untagged }. */
export function filterCounts(items) {
  const rej = items.filter(FILTER_TEST.rejected);
  return {
    all: items.length, buys: items.filter(FILTER_TEST.buys).length, sells: items.filter(FILTER_TEST.sells).length,
    rejected: rej.length, fresh: rej.filter(o => !o.rejection_dismissed).length, untagged: items.filter(FILTER_TEST.untagged).length,
  };
}

// ── remembering the choice (per account, in this browser, until the next session day) ──

export const FILTER_KEY = 'riskTerminal.doneFilter.v1';

export function readFilter(accountKey, day, storage) {
  try {
    const v = JSON.parse((storage || window.localStorage).getItem(FILTER_KEY)) || {};
    const e = v[accountKey];
    return e && e.day === day && FILTERS.includes(e.filter) ? e.filter : 'all';
  } catch (e) { return 'all'; }
}

export function writeFilter(accountKey, day, filter, storage) {
  try {
    const s = storage || window.localStorage;
    const v = JSON.parse(s.getItem(FILTER_KEY)) || {};
    // other days' entries are dropped: a stored choice never outlives its session day
    const keep = Object.fromEntries(Object.entries(v).filter(([, e]) => e && e.day === day));
    if (filter === 'all') delete keep[accountKey]; else keep[accountKey] = { day, filter };
    s.setItem(FILTER_KEY, JSON.stringify(keep));
  } catch (e) { /* a view preference only */ }
}
